import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CheckSquare, Languages, ArrowRightLeft, Headphones, Camera } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { getActiveTrip } from '@/lib/api';
import InstantTools from '@/components/InstantTools';
import { toast } from 'sonner';

/**
 * Dashboard Visiteur - Vue Participant Classique
 * - Itinéraire du jour en priorité
 * - Mes tâches personnelles
 * - Outils pratiques (Instant Tools)
 * - Interface simplifiée, focus sur "suivre"
 */
export default function VisitorDashboard() {
  const navigate = useNavigate();
  const [activeTrip, setActiveTrip] = useState(null);
  const [todayItinerary, setTodayItinerary] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const activeTripRes = await getActiveTrip();
      setActiveTrip(activeTripRes?.data);
      
      // Mock data - à remplacer par vraies APIs
      setTodayItinerary([
        { time: '10:00', title: 'Musée du Louvre', icon: '🏛️' },
        { time: '14:30', title: 'Café de Flore', icon: '☕' },
        { time: '19:00', title: 'Dîner Tour Eiffel', icon: '🍽️' }
      ]);
      
      setMyTasks([
        { id: 1, title: 'Acheter billets métro', completed: false },
        { id: 2, title: 'Réserver resto demain', completed: false }
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="h-48 bg-[#E2E8F0] rounded-2xl animate-pulse" />;
  }

  if (!activeTrip) {
    return (
      <div className="p-6 text-center">
        <p className="text-[#64748B]">Aucun voyage actif</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Trip Header */}
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-[#1E293B]">🌟 {activeTrip.name}</h1>
              <p className="text-sm text-[#64748B]">Organisé par {activeTrip.organizer_name || 'Papa'}</p>
            </div>
            <button
              onClick={() => navigate(`/trips/${activeTrip.trip_id}`)}
              className="text-sm text-blue-600 font-medium hover:underline"
            >
              Voir tout
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Today's Itinerary */}
      <div>
        <h2 className="text-lg font-semibold text-[#1E293B] mb-3 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-500" />
          Aujourd'hui - {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
        </h2>
        
        <div className="space-y-2">
          {todayItinerary.map((item, idx) => (
            <Card key={idx} className="border border-[#E2E8F0]">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="text-3xl">{item.icon}</div>
                <div className="flex-1">
                  <p className="font-medium text-[#1E293B]">{item.title}</p>
                  <p className="text-sm text-[#64748B]">{item.time}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* My Tasks */}
      {myTasks.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-[#1E293B] mb-3 flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-green-500" />
            Mes Tâches ({myTasks.length})
          </h2>
          
          <div className="space-y-2">
            {myTasks.map(task => (
              <Card key={task.id} className="border border-[#E2E8F0]">
                <CardContent className="p-3 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={task.completed}
                    className="w-5 h-5 rounded border-[#E2E8F0]"
                    onChange={() => {/* Handle toggle */}}
                  />
                  <span className={task.completed ? 'line-through text-[#64748B]' : 'text-[#1E293B]'}>
                    {task.title}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Instant Tools - MISE EN AVANT */}
      <div>
        <h2 className="text-lg font-semibold text-[#1E293B] mb-3">Outils Pratiques</h2>
        <InstantTools />
      </div>

      {/* Quick Tools Grid */}
      <div className="grid grid-cols-2 gap-3">
        <ToolCard
          icon={Headphones}
          label="Audio Guide"
          gradient="from-purple-500 to-pink-500"
          onClick={() => navigate('/audio-guides')}
        />
        <ToolCard
          icon={Camera}
          label="C'est quoi ici ?"
          gradient="from-orange-500 to-red-500"
          onClick={() => navigate('/scan')}
        />
      </div>

      {/* Full Itinerary Button */}
      <button
        onClick={() => navigate(`/trips/${activeTrip.trip_id}`)}
        className="w-full py-3 bg-white border-2 border-[#E2E8F0] rounded-xl font-medium text-[#1E293B] hover:bg-[#F1F5F9] transition-colors"
      >
        📅 Voir l'Itinéraire Complet
      </button>
    </div>
  );
}

// Helper Component
function ToolCard({ icon: Icon, label, gradient, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl bg-gradient-to-br ${gradient} text-white hover:opacity-90 transition-opacity`}
    >
      <Icon className="w-6 h-6" />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}
